import { useRef, useState, useCallback } from "react";
import { API_BASE } from "./api";
import { toast } from "sonner";

/**
 * Record audio from mic → POST /api/stt → return transcribed text.
 * Usage:
 *   const { recording, start, stop } = useMicRecorder({ language, onTranscript });
 */
export default function useMicRecorder({ language = "id", onTranscript } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stop = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    try {
      mediaRecorderRef.current.stop();
    } catch (e) {
      // no-op
    }
  }, []);

  const start = useCallback(async () => {
    if (recording) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Mic tidak didukung di browser ini.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = preferred.find((m) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        setRecording(false);
        // stop tracks
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        if (blob.size < 800) return; // ignore tiny blobs
        setTranscribing(true);
        try {
          const form = new FormData();
          const ext = (mimeType.includes("mp4") ? "m4a" : "webm");
          form.append("file", blob, `voice.${ext}`);
          form.append("language", language);
          const token = localStorage.getItem("gracely_token");
          const res = await fetch(`${API_BASE}/stt`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          if (!res.ok) throw new Error("STT failed");
          const data = await res.json();
          if (onTranscript && data.text) {
            // Read blob as base64 to pass back to caller for persistence
            const b64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const s = reader.result || "";
                const idx = s.indexOf(",");
                resolve(idx >= 0 ? s.slice(idx + 1) : s);
              };
              reader.readAsDataURL(blob);
            });
            onTranscript({ text: data.text, audio_b64: b64, audio_mime: blob.type || "audio/webm" });
          }
        } catch (err) {
          toast.error("Gracely tidak dapat mendengarmu — coba lagi.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error("Izin mikrofon ditolak.");
    }
  }, [language, onTranscript, recording]);

  return { recording, transcribing, start, stop };
}
